import { openDB } from 'idb';

import type { Entity, Mutation, SyncRecord } from '@nedelnik/shared';
import type { DBSchema, IDBPDatabase } from 'idb';

/**
 * Локальная копия данных.
 *
 * Клиент держит полную копию (раздел 3.1 архитектуры), поэтому чтение никогда не
 * ходит в сеть, а правка применяется мгновенно. IndexedDB, а не память: вкладку
 * закроют, а очередь неотправленных правок пережить это обязана.
 *
 * Записи всех сущностей лежат в одном хранилище с составным ключом
 * `[entity, key]`. Отдельные хранилища на сущность ничего не дают — выборки всё
 * равно идут по всей копии в памяти, — зато требуют миграции при каждой новой
 * сущности.
 */

export const DB_NAME = 'nedelnik';
export const DB_VERSION = 1;

/** Запись в локальной копии: сущность, её ключ и само состояние. */
export interface StoredRecord {
  entity: Entity;
  key: string;
  record: SyncRecord;
}

/** Выданный сервером блок номеров задач: `next` — следующий свободный. */
export interface NumberLease {
  next: number;
  to: number;
}

/** Мутация в очереди отправки. `n` — порядок, он же ключ. */
export interface QueuedMutation {
  n?: number;
  mutation: Mutation;
}

/**
 * Мутация, которую сервер отверг по содержанию. Она не возвращается в очередь:
 * повтор даст тот же отказ, и очередь встанет колом из-за одной битой записи
 * (раздел 5.3 архитектуры). Лежит здесь, чтобы её можно было показать и разобрать.
 */
export interface QuarantinedMutation {
  op_id: string;
  mutation: Mutation;
  reason: string;
  at: string;
}

export interface NedelnikDb extends DBSchema {
  records: {
    key: [Entity, string];
    value: StoredRecord;
  };
  queue: {
    key: number;
    value: QueuedMutation;
  };
  quarantine: {
    key: string;
    value: QuarantinedMutation;
  };
  meta: {
    key: string;
    value: unknown;
  };
}

export type LocalDb = IDBPDatabase<NedelnikDb>;

export async function openLocalDb(name = DB_NAME): Promise<LocalDb> {
  return openDB<NedelnikDb>(name, DB_VERSION, {
    upgrade(db) {
      db.createObjectStore('records', { keyPath: ['entity', 'key'] });
      db.createObjectStore('queue', { keyPath: 'n', autoIncrement: true });
      db.createObjectStore('quarantine', { keyPath: 'op_id' });
      db.createObjectStore('meta');
    },
  });
}

/**
 * Ключ записи. У всех сущностей это `id`, у настроек — `user_id`: настройки
 * одни на пользователя и своего идентификатора не имеют.
 */
export function recordKey(entity: Entity, record: SyncRecord): string {
  if (entity === 'settings') return String(record['user_id']);
  return String(record.id);
}
